import React from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import {
    ModalContent,
    ModalHeader,
    ModalBody,
    Button,
    HStack,
    SimpleGrid,
    Alert,
    AlertIcon,
    GridItem,
} from '@chakra-ui/react';
import { forms, fields } from '~/pages/projects/meta/data/correspondence.schema';
import { Form, renderField } from '~/components/Form/Form';
import { useModal, useDebounce, useYupValidationResolver, useViewer, usePermission } from '~/hooks';
import { Dokument, Korrespondenz, KorrespondenzDokument, Snippet } from '~/gql/ucpw/graphql';
import { useSubProjectMetadata } from '~/pages/projects/hooks/useSubProjectMetadata';
import { Option } from '~/components/Form/SnippetSelect';
import { useAsset } from '~/hooks/useAsset';
import { join, resolveFormFields, withMaxSerializedLength } from '~/utils';
import { HasPermission } from '~/layout/HasPermission';
import { isEqual } from 'lodash';
import { format } from 'date-fns';
import log from '~/log';

const NO_INVOICE_DOCUMENTS = 'NO_INVOICE_DOCUMENTS';
const GDV_SUPPORTED_FILE_TYPES = ['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'];

export const CorrespondenceModal = (props?: any) => {
    const { hasServiceRequestId } = useSubProjectMetadata();
    const { analyze } = useAsset();
    const [xmlErrors, setXmlErrors] = React.useState<(string | number)[]>([]);
    const [showXmlAlert, setShowXmlAlert] = React.useState(false);
    const [isUploading, setIsUploading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState('');
    const { state, dispatch, onClose } = useModal();
    const {
        createCorrespondenceAndDocumentsMutation,
        updateCorrespondenceAndDocumentsMutation,
        createTaskMutation,
        createTaskFunction,
        deleteTaskFunction,
        isGdv = false,
    } = state?.modals?.props || props || {};
    const subprojectId =
        state?.modals?.props?.subprojectId ||
        props?.subprojectId ||
        state?.row?.subprojekt?.id ||
        state?.rows?.row?.subprojekt?.id;

    const viewer = useViewer();

    const editorEmployeeId = viewer.app?.mitarbeiterId;

    const { row: correspondence = {}, rowId } = state?.rows || {};

    const formFields = resolveFormFields(forms.correspondence, fields.correspondence);
    const id = useDebounce(rowId, 200);

    const defaultValues = {
        ...formFields.defaultValues,
        ...formFields.toForm({
            ...correspondence,
            ...(!rowId && editorEmployeeId && { mitarbeiterIdSachbearbeiter: editorEmployeeId }),
        }),
        time: format(
            correspondence?.zeit ? new Date(correspondence?.zeit) : new Date(),
            'dd.MM.yyyy HH:mm'
        ),
    };

    const {
        handleSubmit,
        control,
        register,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues,
        shouldFocusError: true,
        resolver: useYupValidationResolver(
            yup.object({
                typeSnippetId: yup.object().nullable().required('Typ ist erforderlich'),
                subtypSnippetId: yup
                    .object()
                    .nullable()
                    .when('typeSnippetId', {
                        is: (typeSnippetId: any) => typeSnippetId?.label === 'GDV-Nachricht',
                        then: (schema) =>
                            schema.required('Subtyp ist erforderlich für GDV-Nachricht'),
                        otherwise: (schema) => schema.nullable(),
                    }),
                document: yup.array().when('typeSnippetId', {
                    is: (typeSnippetId: any) => typeSnippetId?.label === 'GDV-Nachricht',
                    then: (schema) =>
                        schema.test(
                            'gdv-file-types',
                            `Für GDV-Nachrichten sind nur folgende Dateitypen erlaubt: ${GDV_SUPPORTED_FILE_TYPES.join(
                                ', '
                            )}`,
                            (documents) => {
                                if (!documents || documents.length === 0) {
                                    return true; // Allow empty documents, other validation will handle required
                                }
                                return documents.every((doc: any) => {
                                    const mimeType = doc?.dokument?.mimeType;
                                    if (!mimeType) {
                                        return true; // Allow documents without mimeType (existing uploads)
                                    }
                                    return GDV_SUPPORTED_FILE_TYPES.includes(mimeType);
                                });
                            }
                        ),
                    otherwise: (schema) => schema.nullable(),
                }),
                // korrespondenz.freitext VARCHAR(10000) — Limit gilt für das serialisierte JSON (ADR-0004)
                comment: withMaxSerializedLength(yup.array(), 10000).test('comment', 'Bemerkung ist erforderlich', (value: any) => {
                    console.log('yup', value);
                    return !isEqual(value, [
                        {
                            type: 'paragraph',
                            children: [
                                {
                                    text: '',
                                },
                            ],
                        },
                    ]);
                }),
            })
        ),
    });
    const watchFields = watch();

    log.debug('CorrespondenceModal', {
        id,
        rowId,
        correspondence,
        subprojectId,
        state,
        defaultValues,
        formFields,
        watchFields,
        errors,
    });

    /** Permit */
    const hasEditAllCorrespondence = viewer.hasPermission(
        'additional.editAllCorrespondence',
        'enabled'
    );
    const { canCreate, canEdit, canView } = usePermission('project.correspondence');
    const readOnly = rowId && canView && !(canCreate || canEdit || hasEditAllCorrespondence);
    const canEditCorrespondence = React.useMemo(() => {
        if (!rowId) return (canCreate || hasEditAllCorrespondence) && !isGdv;
        if (hasEditAllCorrespondence && !isGdv) return true;
        const isOwner = correspondence?.mitarbeiterIdUrheber === editorEmployeeId;
        const hasNoOwner = correspondence?.mitarbeiterIdUrheber === null;
        return canEdit && !isGdv && (isOwner || hasNoOwner);
    }, [
        rowId,
        canCreate,
        canEdit,
        hasEditAllCorrespondence,
        isGdv,
        correspondence?.mitarbeiterIdUrheber,
        editorEmployeeId,
    ]);

    const modalConfig = React.useMemo(() => {
        const isViewMode = readOnly || !canEditCorrespondence || isGdv;
        const isEditMode = rowId && canEditCorrespondence;

        return {
            title: isViewMode
                ? 'Korrespondenz ansehen'
                : isEditMode
                ? 'Korrespondenz bearbeiten'
                : 'Neue Korrespondenz',
            cancelButtonLabel: isViewMode ? 'Schließen' : 'Abbrechen',
            cursor: isViewMode ? 'not-allowed' : 'auto',
        };
    }, [readOnly, canEditCorrespondence, isGdv, rowId]);

    function parseCustomDate(dateStr: string) {
        const [datePart, timePart] = dateStr.split(' ');
        const [day, month, year] = datePart.split('.').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);

        return new Date(year, month - 1, day, hour, minute);
    }

    const validateDocumentsXML = React.useCallback(
        async (values: any) => {
            const { typeSnippetId, subtypSnippetId, document } = values;
            const errors: (string | number)[] = [];

            if (typeSnippetId?.label === 'GDV-Nachricht' && subtypSnippetId?.label === 'Rechnung') {
                // Check if there's at least one document with typSnippet label 'Rechnung'
                const rechnungDocuments =
                    document?.filter(
                        (
                            doc: KorrespondenzDokument & {
                                dokument: Dokument & { typSnippet: Snippet & { label: string } };
                            }
                        ) => {
                            const { dokument } = doc || {};
                            return dokument?.typSnippet?.label === 'Rechnung';
                        }
                    ) || [];

                if (rechnungDocuments.length === 0) {
                    // Return a special error code to indicate no Rechnung documents found
                    return [NO_INVOICE_DOCUMENTS];
                }

                // Validate XML for each Rechnung document
                for (const [idx, doc] of document.entries()) {
                    const { dokument } = doc || {};
                    if (dokument?.typSnippet?.label === 'Rechnung' && dokument?.pfad) {
                        const { status } = await analyze(dokument.pfad);
                        const hasXML = status === 'valid';
                        if (!hasXML) {
                            errors.push(idx);
                        }
                    }
                }
            }

            return errors;
        },
        [analyze]
    );

    const onSubmit = React.useCallback(
        async (values: any) => {
            log.debug('onSubmit.values', JSON.stringify(values, null, 2));

            // Validate XML before proceeding
            const xmlValidationErrors = await validateDocumentsXML(values);
            if (xmlValidationErrors.length > 0) {
                setXmlErrors(xmlValidationErrors);
                setShowXmlAlert(true);
                log.warn(
                    'onSubmit.validation',
                    'Cannot save: XML validation failed',
                    xmlValidationErrors
                );
                return;
            }

            const addValues = {
                subprojectId: subprojectId || (correspondence as Korrespondenz).subprojektId,
            };
            const {
                korrespondenzDokument = [],
                zeit,
                ...data
            } = formFields.toGql(values, addValues) as any;
            log.debug('onSubmit.data', JSON.stringify(data, null, 2));

            console.log('onSubmit.compare', { data, zeit, correspondence });

            const documents = korrespondenzDokument.map((document: any) => {
                const { dokument } = document || {};
                const typSnippet = dokument?.typSnippet;
                return {
                    ...(typSnippet && {
                        typSnippetId: dokument?.typSnippet?.id || dokument?.typSnippet?.value,
                    }),
                    ...(dokument?.angebotId && { angebotId: dokument?.angebotId }),
                    subprojektId: subprojectId,
                    name: dokument?.name || '',
                    pfad: dokument?.pfad || '',
                    beschreibung: dokument?.beschreibung || '',
                    uploadDatum: dokument?.uploadDatum || new Date(),
                };
            });

            const { documentsToUpdate, documentsToCreate } = korrespondenzDokument.reduce(
                (acc: any, document: any) => {
                    const { dokumentId: id, dokument } = document || {};
                    const typSnippet = dokument?.typSnippet;
                    const data = {
                        ...(typSnippet && {
                            typSnippetId: dokument?.typSnippet?.id || dokument?.typSnippet?.value,
                        }),
                        subprojektId: subprojectId,
                        name: dokument?.name || '',
                        pfad: dokument?.pfad || '',
                        beschreibung: dokument?.beschreibung || '',
                        uploadDatum: dokument?.uploadDatum || new Date(),
                    };

                    if (id) {
                        acc.documentsToUpdate.push({ id, data });
                    } else {
                        acc.documentsToCreate.push(data);
                    }

                    return acc;
                },
                { documentsToUpdate: [], documentsToCreate: [] }
            );

            const response = id
                ? await updateCorrespondenceAndDocumentsMutation({
                      variables: {
                          id,
                          version: correspondence?.version,
                          forceOverwrite: true,
                          correspondence: {
                              ...data,
                              ...(values?.typeSnippetId?.label !== 'GDV-Nachricht' && {
                                  subtypSnippetId: 0,
                              }),
                              subprojektId: subprojectId,
                          },
                          documentsToUpdate,
                          documentsToCreate,
                      },
                  })
                : await createCorrespondenceAndDocumentsMutation({
                      variables: {
                          correspondence: {
                              ...data,
                              mitarbeiterIdUrheber: editorEmployeeId,
                              subprojektId: subprojectId,
                              zeit: parseCustomDate(zeit),
                          },
                          documents,
                      },
                  });
            log.debug('onSubmit.response', JSON.stringify(response, null, 2));
        },
        [id, editorEmployeeId, subprojectId, correspondence, validateDocumentsXML]
    );

    const onSubmitWithOnClose = async (values: any) => {
        setSubmitting('submit');
        await onSubmit(values);

        if (xmlErrors.length === 0) {
            onReset();
        } else {
            setSubmitting('');
        }
    };

    const onSubmitWithRedirect = async (values: any) => {
        setSubmitting('submit-and-task');
        await onSubmit(values);

        if (xmlErrors.length === 0) {
            dispatch?.({ type: 'resetState' });
            dispatch?.({
                type: 'setModal',
                data: {
                    modal: 'TaskModal',
                    props: {
                        createMutation: createTaskMutation,
                        createTaskFunction,
                        deleteTaskFunction,
                    },
                },
            });
            dispatch?.({
                type: 'setRow',
                data: { row: { original: { subprojekt: correspondence?.subprojekt || {} } } },
            });
            dispatch?.({ type: 'formState', data: values });
        }
        setSubmitting('');
    };

    const onReset = () => {
        dispatch?.({ type: 'resetState' });
        onClose?.();
        setSubmitting('');
        setShowXmlAlert(false);
    };

    React.useEffect(() => {
        const subscription = watch(async (values, { type }) => {
            console.log('Changed:', values, type);

            const errors = await validateDocumentsXML(values);
            setXmlErrors(errors);

            if (showXmlAlert) {
                setShowXmlAlert(false);
            }

            if (values?.typeSnippetId?.label !== 'GDV-Nachricht' && values?.subtypSnippetId) {
                setValue('subtypSnippetId', undefined);
            }
        });

        return () => subscription.unsubscribe();
    }, [watch, validateDocumentsXML, showXmlAlert]);

    return (
        <ModalContent maxWidth="container.md" rounded="none">
            <ModalHeader
                justifyContent="space-between"
                alignItems="center"
                display="flex"
                borderBottomWidth={1}
                borderColor="gray.200"
                mb={6}
                p={5}
            >
                {modalConfig.title}
                <HStack>
                    <Button
                        data-test-id="button-cancel"
                        isDisabled={!!submitting}
                        variant="outline"
                        onClick={onReset}
                    >
                        {modalConfig.cancelButtonLabel}
                    </Button>
                    {canEditCorrespondence && (
                        <>
                            <HasPermission resource="project.tasks" permission="create">
                                <Button
                                    isLoading={submitting === 'submit-and-task'}
                                    isDisabled={
                                        submitting === 'submit' || showXmlAlert || isUploading
                                    }
                                    data-test-id="button-save-correspondence-task-next"
                                    variant="outline"
                                    onClick={handleSubmit(onSubmitWithRedirect)}
                                >
                                    Speichern + Aufgabe
                                </Button>
                            </HasPermission>
                            <Button
                                isLoading={submitting === 'submit'}
                                isDisabled={
                                    submitting === 'submit-and-task' || showXmlAlert || isUploading
                                }
                                data-test-id="button-save-correspondence"
                                colorScheme="blue"
                                onClick={handleSubmit(onSubmitWithOnClose)}
                            >
                                Speichern
                            </Button>
                        </>
                    )}
                </HStack>
            </ModalHeader>
            <ModalBody cursor={modalConfig.cursor}>
                <Form onSubmit={handleSubmit(onSubmitWithOnClose)}>
                    <SimpleGrid spacing={4} columns={6} mb={6}>
                        {formFields.fields.map((field: any) => (
                            <React.Fragment key={field.name}>
                                {renderField({
                                    field,
                                    control,
                                    register,
                                    errors,
                                    watchFields,
                                    context: {
                                        errors,
                                        subprojectId,
                                        canEditCorrespondence,
                                        ...(showXmlAlert && { xmlErrors }),
                                        onUploadLoadingChange: setIsUploading,
                                        typeSnippetId: watchFields.typeSnippetId,
                                        hasServiceRequestId,
                                        ...(watchFields.typeSnippetId?.label ===
                                            'GDV-Nachricht' && {
                                            supportedFileTypes: GDV_SUPPORTED_FILE_TYPES,
                                        }),
                                        mapOptions: ({ label, ...snippet }: Option) => {
                                            const ranks = {
                                                Angebot: 0,
                                                Terminmitteilung: 1,
                                                'Zwischeninfo - Zur Ablage': 2,
                                                'Zwischeninfo - Reaktion erforderlich': 3,
                                                Rechnung: 4,
                                                Projektabschluss: 5,
                                                Nachtrag: 6,
                                                Stornierung: 7,
                                            };
                                            return {
                                                ...snippet,
                                                label,
                                                rank: ranks[label as keyof typeof ranks],
                                            };
                                        },
                                        sortOptions: (a: Option, b: Option) => {
                                            return a?.rank && b?.rank ? a?.rank - b?.rank : 0;
                                        },
                                        filterOptions: ({ label, value }: Option) => {
                                            return correspondence?.subtypSnippetId === value
                                                ? true
                                                : !['Angebot', 'Stornierung', 'Nachtrag'].includes(
                                                      label
                                                  );
                                        },
                                        meta: {
                                            employeeId: {
                                                value: correspondence?.sachbearbeiter?.id,
                                                label: join([
                                                    correspondence?.sachbearbeiter?.name,
                                                    correspondence?.sachbearbeiter?.vorname,
                                                ]),
                                            },
                                        },
                                    },
                                })}
                            </React.Fragment>
                        ))}
                    </SimpleGrid>
                </Form>
                {showXmlAlert && xmlErrors.length > 0 && (
                    <GridItem colSpan={2} mb={6}>
                        <Alert status="warning">
                            <AlertIcon />
                            {xmlErrors.includes(NO_INVOICE_DOCUMENTS)
                                ? 'Für eine GDV-Nachricht vom Subtyp "Rechnung" muss mindestens ein Dokument vom Typ "Rechnung" angehängt werden.'
                                : xmlErrors.length === 1
                                ? 'Die beigefügte Rechnung hat kein valides XML.'
                                : 'Die beigefügten Rechnungen haben keine valides XML.'}
                        </Alert>
                    </GridItem>
                )}
            </ModalBody>
        </ModalContent>
    );
};
